import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function formatTime(time) {
  if (!time) return '--:--';
  return time.substring(0, 5);
}

function formatDeadline(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Lima'
  });
}

// ── Pantallas de resultado ────────────────────────────────────────────────────

function ScreenLoading() {
  return (
    <div style={s.card}>
      <div style={s.bigIcon}>⏳</div>
      <p style={s.muted}>Cargando información de tu pedido...</p>
    </div>
  );
}

function ScreenError({ message }) {
  return (
    <div style={s.card}>
      <div style={s.bigIcon}>❌</div>
      <h2 style={s.title}>Link inválido</h2>
      <p style={s.body}>{message || 'Este link es inválido o ya fue utilizado.'}</p>
    </div>
  );
}

function ScreenExpired({ currentWindow }) {
  return (
    <div style={s.card}>
      <div style={s.bigIcon}>⏰</div>
      <h2 style={s.title}>Tiempo expirado</h2>
      <p style={s.body}>El tiempo para responder ha expirado.</p>
      <p style={s.body}>Nuestro equipo se comunicará contigo para coordinar la entrega.</p>
      {currentWindow && (
        <div style={{ ...s.windowBadge, ...s.windowNeutral, marginTop: 16 }}>
          {formatTime(currentWindow.start)} - {formatTime(currentWindow.end)}
        </div>
      )}
    </div>
  );
}

function ScreenAccepted({ newWindow }) {
  return (
    <div style={s.card}>
      <div style={s.bigIcon}>✅</div>
      <h2 style={{ ...s.title, color: '#52c41a' }}>¡Confirmado!</h2>
      <p style={s.body}>Tu pedido será entregado entre las</p>
      {newWindow && (
        <div style={{ ...s.windowBadge, ...s.windowGreen, marginTop: 8 }}>
          {formatTime(newWindow.start)} - {formatTime(newWindow.end)}
        </div>
      )}
      <p style={{ ...s.body, marginTop: 20 }}>
        Te avisaremos cuando esté en camino 🚚
      </p>
    </div>
  );
}

function ScreenRejected({ currentWindow }) {
  return (
    <div style={s.card}>
      <div style={s.bigIcon}>👍</div>
      <h2 style={s.title}>Entendido</h2>
      <p style={s.body}>Mantenemos tu pedido en el horario original.</p>
      {currentWindow && (
        <div style={{ ...s.windowBadge, ...s.windowNeutral, marginTop: 8 }}>
          {formatTime(currentWindow.start)} - {formatTime(currentWindow.end)}
        </div>
      )}
      <p style={{ ...s.body, marginTop: 20 }}>
        Nuestro equipo se comunicará contigo si hay algún inconveniente 🙌
      </p>
    </div>
  );
}

// ── Pantalla principal: propuesta de cambio ───────────────────────────────────

function ScreenInfo({ info, onAccept, onReject, actionLoading }) {
  const deadline = formatDeadline(info.responseDeadline);

  return (
    <div style={s.card}>
      <div style={s.logo}>🥦 vegetales</div>

      <h2 style={s.title}>Hola, {info.customerName} 👋</h2>

      <p style={s.body}>Tu pedido está programado para las:</p>
      <div style={{ ...s.windowBadge, ...s.windowNeutral }}>
        {formatTime(info.currentWindow.start)} - {formatTime(info.currentWindow.end)}
      </div>

      <div style={s.proposalBox}>
        <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>
          Queremos proponerte un cambio de horario:
        </p>
        <div style={{ ...s.windowBadge, ...s.windowBlue, marginTop: 12 }}>
          {formatTime(info.proposedWindow.start)} - {formatTime(info.proposedWindow.end)}
        </div>
      </div>

      {deadline && (
        <p style={s.deadlineText}>⏰ Responde antes de las {deadline}</p>
      )}

      <div style={s.actions}>
        <button
          style={{ ...s.btn, ...s.btnAccept, ...(actionLoading ? s.btnDisabled : {}) }}
          onClick={onAccept}
          disabled={actionLoading}
        >
          {actionLoading ? '...' : '✔  Acepto el nuevo horario'}
        </button>
        <button
          style={{ ...s.btn, ...s.btnReject, ...(actionLoading ? s.btnDisabled : {}) }}
          onClick={onReject}
          disabled={actionLoading}
        >
          {actionLoading ? '...' : 'Prefiero mi horario original'}
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function ConfirmationView() {
  var params = useParams();
  var hash = params.hash;

  var _s1 = useState('loading');
  var screen = _s1[0]; var setScreen = _s1[1];

  var _s2 = useState(null);
  var info = _s2[0]; var setInfo = _s2[1];

  var _s3 = useState(null);
  var errorMsg = _s3[0]; var setErrorMsg = _s3[1];

  var _s4 = useState(null);
  var newWindow = _s4[0]; var setNewWindow = _s4[1];

  var _s5 = useState(false);
  var actionLoading = _s5[0]; var setActionLoading = _s5[1];

  useEffect(function() {
    axios.get(API_BASE_URL + '/c/' + hash)
      .then(function(res) {
        if (res.data.deadlineExpired) {
          setInfo(res.data);
          setScreen('expired');
        } else {
          setInfo(res.data);
          setScreen('info');
        }
      })
      .catch(function(err) {
        if (err.response && err.response.status === 404) {
          setErrorMsg('Este link es inválido o ya fue utilizado.');
        } else {
          setErrorMsg('Ocurrió un error. Por favor intenta nuevamente.');
        }
        setScreen('error');
      });
  }, [hash]);

  var handleAccept = function() {
    setActionLoading(true);
    axios.post(API_BASE_URL + '/c/' + hash + '/accept')
      .then(function(res) {
        setNewWindow(res.data.newWindow || null);
        setScreen('accepted');
      })
      .catch(function(err) {
        if (err.response && err.response.status === 410) {
          setScreen('expired');
        } else {
          setErrorMsg('No se pudo procesar tu confirmación. Por favor intenta nuevamente.');
          setScreen('error');
        }
        setActionLoading(false);
      });
  };

  var handleReject = function() {
    setActionLoading(true);
    axios.post(API_BASE_URL + '/c/' + hash + '/reject')
      .then(function() {
        setScreen('rejected');
      })
      .catch(function() {
        setErrorMsg('No se pudo procesar tu respuesta. Por favor intenta nuevamente.');
        setScreen('error');
        setActionLoading(false);
      });
  };

  return (
    <div style={s.page}>
      {screen === 'loading'  && <ScreenLoading />}
      {screen === 'error'    && <ScreenError message={errorMsg} />}
      {screen === 'expired'  && <ScreenExpired currentWindow={info && info.currentWindow} />}
      {screen === 'accepted' && <ScreenAccepted newWindow={newWindow} />}
      {screen === 'rejected' && <ScreenRejected currentWindow={info && info.currentWindow} />}
      {screen === 'info'     && (
        <ScreenInfo
          info={info}
          onAccept={handleAccept}
          onReject={handleReject}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}

// ── Estilos inline (sin dependencia de CSS externo) ───────────────────────────

var s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    padding: 16,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '36px 28px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
    textAlign: 'center'
  },
  logo: {
    fontSize: 20,
    fontWeight: 800,
    color: '#52c41a',
    letterSpacing: 1,
    marginBottom: 28
  },
  bigIcon: {
    fontSize: 52,
    marginBottom: 16
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 12px',
    color: '#1a1a1a'
  },
  body: {
    color: '#555',
    margin: '6px 0',
    lineHeight: 1.6,
    fontSize: 15
  },
  muted: {
    color: '#999',
    marginTop: 8
  },
  windowBadge: {
    display: 'inline-block',
    padding: '10px 28px',
    borderRadius: 24,
    fontWeight: 700,
    fontSize: 20,
    margin: '4px 0',
    letterSpacing: 1
  },
  windowGreen: {
    background: '#f6ffed',
    color: '#52c41a',
    border: '2px solid #b7eb8f'
  },
  windowBlue: {
    background: '#e6f7ff',
    color: '#1890ff',
    border: '2px solid #91d5ff'
  },
  windowNeutral: {
    background: '#fafafa',
    color: '#666',
    border: '2px solid #e8e8e8'
  },
  proposalBox: {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 10,
    padding: '20px 16px',
    margin: '20px 0 12px'
  },
  deadlineText: {
    color: '#fa8c16',
    fontWeight: 600,
    fontSize: 13,
    margin: '10px 0 20px'
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 24
  },
  btn: {
    padding: '15px 20px',
    borderRadius: 10,
    border: 'none',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    transition: 'opacity 0.2s'
  },
  btnAccept: {
    background: '#52c41a',
    color: '#fff'
  },
  btnReject: {
    background: '#fff',
    color: '#555',
    border: '1.5px solid #d9d9d9'
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  }
};
